import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from '../../../../components/Button/Button';
import Input from '../../../../components/Input/Input';
import PermissionManager from '../PermissionOption/PermissionManager';
import Modal from '../../../../components/Modal/Modal';
import './EditRole.css';
//services
import { role } from '../../../../services/role';
import { modulePermissions } from '../../../../utils/modulePermissions';

const EditRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentUser = useSelector(state => state.user.userData);
  const [permissions, setPermissions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState({ text: '', highlight: '' });
  const [modalMessage, setModalMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(false);
  const [isCurrentUserRole, setIsCurrentUserRole] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    user: '',
    description: '',
    permissions: {}
  });

  useEffect(() => {
    fetchRole();
  }, []);

  useEffect(() => {
    // Verificar si el rol que se está editando es el mismo del usuario actual
    if (currentUser?.roles?.[0] === formData.name) {
      setIsCurrentUserRole(true);
    }
  }, [currentUser, formData.name]);

  function groupModules(data) {
    // 1. Separar los elementos principales (que tienen 'management') y los demás
    const mainModules = data.filter(item => item.name.includes('_management'));
    const otherModules = data.filter(item => !item.name.includes('_management'));
  
    // 2. Crear la estructura agrupada
    const groupedData = mainModules.map(mainModule => {
      const prefix = mainModule.name.split('_')[0];
      
      // Encontrar todos los módulos que comienzan con el mismo prefijo (incluyendo al padre)
      const allRelatedModules = data.filter(item => 
        item.name.startsWith(prefix + '_')
      );
  
      // Clonar el padre sin permisos para el nivel superior
      const parentWithoutPermissions = {
        ...mainModule,
        permissions: undefined // Eliminamos los permisos del nivel padre
      };
  
      // Para los hijos, mantenemos todos los módulos relacionados (incluyendo al padre completo)
      const children = allRelatedModules.map(module => ({
        ...module,
        // Marcamos si es el padre para referencia
        isParent: module.id === mainModule.id
      }));
  
      return {
        ...parentWithoutPermissions,
        children: children.length > 0 ? children : undefined
      };
    });
  
    // 3. Añadir módulos que no tienen un padre 'management'
    const remainingModules = otherModules.filter(child => 
      !groupedData.some(group => 
        group.children && group.children.some(c => c.id === child.id)
      )
    ).map(item => ({ ...item, permissions: undefined })); // También eliminamos permisos de estos
  
    return [...groupedData, ...remainingModules];
  }

  const fetchRole = async () => {
    try {
      // Obtener los permisos del rol
      const response = await role.getRolePermissions(id);
      console.log('Role permissions response:', response);

      if (response.status === 'success') {
        const { name, description, user, modules } = response.data;
        console.log('Role response:', JSON.stringify({ name, description, user, modules }, null, 2));
        
        // Obtener todos los módulos y permisos disponibles
        const allModules = await role.rolesWithPermissions();
        console.log('All modules response:', allModules);

        if (allModules.status === 'success' && Array.isArray(allModules.data)) {
          // Crear un mapa de permisos por ID
          const permissionsById = new Map();
          const currentPermissions = new Set();

          // Crear un mapa de permisos por módulo
          const modulePermissions = new Map();
          if (Array.isArray(modules)) {
            console.log('Processing modules:', JSON.stringify(modules, null, 2));
            modules.forEach(module => {
              console.log('Processing module:', JSON.stringify(module, null, 2));
              const permissions = [];
              if (Array.isArray(module.permissions)) {
                console.log(`Processing permissions for module ${module.id}:`, module.permissions);
                module.permissions.forEach(permission => {
                  console.log('Processing permission:', permission);
                  // Si es un objeto con id o un número, agregarlo
                  if (typeof permission === 'object' && permission.id) {
                    permissions.push(permission.id);
                    console.log(`Added permission ID: ${permission.id}`);
                  } else if (typeof permission === 'number') {
                    permissions.push(permission);
                    console.log(`Added permission number: ${permission}`);
                  }
                });
              }
              // Usar el id del módulo
              if (module.id) {
                modulePermissions.set(module.id, permissions);
                console.log(`Set permissions for module ${module.id}:`, permissions);
              } else {
                console.log('Module missing id:', module);
              }
            });
          }
          console.log('Final module permissions:', Object.fromEntries(modulePermissions));

          // Agrupar los módulos usando la misma función que NewRole
          const groupedModules = groupModules(allModules.data);
          
          // Inicializar permisos como array de módulos
          const initialPermissions = [];
          groupedModules.forEach(module => {
            if (Array.isArray(module.children)) {
              module.children.forEach(child => {
                // Obtener los permisos para este módulo
                const permissions = modulePermissions.get(child.id) || [];
                console.log('Processing child:', {
                  childId: child.id,
                  permissions,
                  hasPermissions: permissions.length > 0
                });

                initialPermissions.push({
                  module_id: child.id,
                  permissions: permissions
                });
              });
            }
          });
          
          console.log('Initial permissions:', initialPermissions);

          // Guardar el mapa de permisos en el estado
          window.permissionsById = permissionsById;

          console.log('Processed permissions:', { groupedModules, initialPermissions, modules });
          
          setPermissions(groupedModules);
          setFormData({
            name: name || '',
            user: user || '',
            description: description || '',
            permissions: initialPermissions
          });
        }
      }
    } catch (error) {
      console.error('Error fetching role:', error);
      setModalTitle({ text: 'Error', highlight: 'Loading Role' });
      setModalMessage(error.response?.data?.detail || 'There was an error loading the role');
      setError(true);
      setIsModalOpen(true);
    }
  };

  const validateField = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    if (value.length < 3) {
      return `${fieldName} must be at least 3 characters long`;
    }
    if (value.length > 250) {
      return `${fieldName} must not exceed 250 characters`;
    }
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validar el campo mientras se escribe
    const error = validateField(value, name.charAt(0).toUpperCase() + name.slice(1));
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };



  const handleModalClose = () => {
    setIsModalOpen(false);
    if (error) {
      setError(false);
      return;
    }
    if (isCurrentUserRole) {
      // Si es el rol del usuario actual, redirigir al login
      modulePermissions.clearPermissions();
      navigate('/');
    } else {
      // Si no, redirigir a la lista de roles
      navigate('/dashboard/roles');
    }
  };

  const handleCancel = () => {
    if (isCurrentUserRole) {
      navigate('/');
      return;
    }
    if (error) {
      setError(false);
      setIsModalOpen(false);
      return;
    }
    navigate('/dashboard/roles');
  };

  const handleSave = async () => {
    // Validar campos
    const nameError = validateField(formData.name, 'Name');
    const descriptionError = validateField(formData.description, 'Description');

    if (nameError || descriptionError) {
      setModalTitle({ text: 'Validation', highlight: 'Error' });
      setModalMessage(nameError || descriptionError);
      setError(true);
      setIsModalOpen(true);
      return;
    }

    // Filtrar solo los módulos que tienen permisos seleccionados
    const modulesWithPermissions = formData.permissions
      .filter(module => module.permissions && module.permissions.length > 0)
      .map(module => ({
        module_id: module.module_id,
        permissions: module.permissions
      }));

    const data = {
      name: formData.name,
      description: formData.description,
      modules: modulesWithPermissions
    };

    console.log('Data to save:', data);

    try {
      const response = await role.updateRole(id, data);
      if (response.status === 'success') {
        if (isCurrentUserRole) {
          setModalTitle({ text: 'Role', highlight: 'Updated' });
          setModalMessage('The role has been updated successfully. Since this is your current role, you need to log out for the changes to take effect.');
        } else {
          setModalTitle({ text: 'Role', highlight: 'Updated' });
          setModalMessage('The role has been updated successfully');
        }
        setError(false);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      setModalTitle({ text: 'Error', highlight: 'Updating Role' });
      setModalMessage(error.response?.data?.detail || 'There was an error updating the role');
      setError(true);
      setIsModalOpen(true);
    }
  };

  const handlePermissionChange = (newValues) => {
    console.log('EditRole change:', newValues);
    setFormData(prev => ({
      ...prev,
      permissions: newValues
    }));
  };

  return (
    <div className="new-role-page">
      <div className="new-role-container">
        <h1 className="page-title">
          <span className="highlight">Edit</span> Role
        </h1>

        <div className="form-section">
          <div className="input-group-roles">
            <div className="form-field-roles">
              <label>Name of role*</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange({ target: { name: 'name', value: e.target.value } })}
                placeholder="Enter the name of the role"
                required
                size="small"
                error={errors.name}
              />
            </div>
            <div className="form-field-roles">
              <label>User*</label>
              <Input
                type="text"
                value={formData.user}
                onChange={(e) => handleInputChange({ target: { name: 'user', value: e.target.value } })}
                placeholder="Enter the user"
                required
                size="small"
              />
            </div>
            <div className="form-field-roles">
              <label>Role description*</label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange({ target: { name: 'description', value: e.target.value } })}
                placeholder="Enter description"
                required
                size="small"
                error={errors.description}
              />
            </div>
          </div>

          <div className="permissions-section">
            <div className="permissions-container">
              <PermissionManager
                permissions={permissions}
                currentValues={formData.permissions}
                onChange={handlePermissionChange}
              />
            </div>
          </div>

          <div className="form-actions-roles">
            <Button
              onClick={() => navigate('/dashboard/roles')}
              variant="warning"
              fullWidth
              borderRadius={20}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="primary"
              fullWidth
              borderRadius={20}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        title={modalTitle}
        message={modalMessage}
        onConfirm={handleCancel}
      />
    </div>
  );
};

export default EditRole;
